"use client";
import React from "react";
import { Badge, Box, Chip, Stack, Tooltip, Typography } from "@mui/material";
import { useSelector } from "react-redux";

const SORT_LABELS = {
  cheapest_total : 'Cheapest total',
  cheapest_kg    : 'Cheapest /kg',
  fastest        : 'Fastest',
  slowest        : 'Slowest',
};

const SORT_ICONS = {
  cheapest_total : '₹',
  cheapest_kg    : '⚖',
  fastest        : '⚡',
  slowest        : '⏱',
};

const FilterChips = ({ data, visibleCount = 1 }) => {
  const filtersdata = useSelector((state) => state?.filter?.filterdata);
  // console.log(data, "data123");

  /* ── Slice Visible / Hidden ── */
  const visibleAirlines = data?.airlines?.slice(0, visibleCount) ?? [];
  const visibleServices = data?.services?.slice(0, visibleCount) ?? [];

  const totalAirlines = data?.airlines?.length ?? 0;
  const totalServices = data?.services?.length ?? 0;

  /* ── Other Filter Count (stops / rate / price / duration) ── */
  const filterCount = [
    data?.stops?.length ? 1 : 0,

    data?.ratePerKg &&
    (data.ratePerKg.min !== filtersdata?.ratePerKiloRange?.min ||
      data.ratePerKg.max !== filtersdata?.ratePerKiloRange?.max)
      ? 1
      : 0,

    data?.totalPrice &&
    (data.totalPrice.min !== filtersdata?.priceRange?.min ||
      data.totalPrice.max !== filtersdata?.priceRange?.max)
      ? 1
      : 0,

    data?.journeyDuration &&
    (data.journeyDuration.minHours !==
      Math.floor(filtersdata?.travelTimeRange?.min / 60) ||
      data.journeyDuration.maxHours !==
        Math.ceil(filtersdata?.travelTimeRange?.max / 60))
      ? 1
      : 0,
  ].reduce((a, b) => a + b, 0);

  /* ── Shared sx ── */
  const chipSx = {
    fontSize: "0.68rem",
    height: 20,
    cursor: "pointer",
    "& .MuiChip-label": { px: 0.5 },
  };

  const badgeSx = {
    "& .MuiBadge-badge": {
      fontSize: "0.54rem",
      height: 15,
      minWidth: 15,
      padding: "0 4px",
      top: 3,
      right: 3,
      fontWeight: 500,
      color: "black",
      border: "1px solid black",
      backgroundColor: "#ffaf00",
    },
  };

  /* ── Airline tooltip — all airlines ── */
  const airlineTooltip = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      <Typography variant="caption" fontWeight={700}>
        Airlines:
      </Typography>
      {data?.airlines?.map((a, i) => (
        <Typography key={i} variant="caption">
          • {a}
        </Typography>
      ))}
    </Box>
  );

  /* ── Filters tooltip — stops / rate / price / duration ── */
  const filtersTooltip = (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
      {data?.stops?.length ? (
        <Typography variant="caption">
          <strong>Stops:</strong> {data.stops.join(", ")}
        </Typography>
      ) : null}

      {data?.ratePerKg &&
      (data.ratePerKg.min !== filtersdata?.ratePerKiloRange?.min ||
        data.ratePerKg.max !== filtersdata?.ratePerKiloRange?.max) ? (
        <Typography variant="caption">
          <strong>Rate:</strong> ₹{data.ratePerKg.min} – ₹{data.ratePerKg.max} /Kg
        </Typography>
      ) : null}

      {data?.totalPrice &&
      (data.totalPrice.min !== filtersdata?.priceRange?.min ||
        data.totalPrice.max !== filtersdata?.priceRange?.max) ? (
        <Typography variant="caption">
          <strong>Total:</strong> ₹{data.totalPrice.min} – ₹{data.totalPrice.max}
        </Typography>
      ) : null}

      {data?.journeyDuration &&
      (data.journeyDuration.minHours !==
        Math.floor(filtersdata?.travelTimeRange?.min / 60) ||
        data.journeyDuration.maxHours !==
          Math.ceil(filtersdata?.travelTimeRange?.max / 60)) ? (
        <Typography variant="caption">
          <strong>Duration:</strong> {data.journeyDuration.minHours}hr –{" "}
          {data.journeyDuration.maxHours}hr
        </Typography>
      ) : null}
    </Box>
  );

  return (
    <Box sx={{ width: "100%" }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 1.5 }}
        flexWrap="wrap"
        useFlexGap
        alignItems="center"
        sx={{ width: "100%" }}
      >
        {/* Airline Chip */}
        {visibleAirlines.length > 0 && (
          <Tooltip title={airlineTooltip} arrow placement="top">
            <Badge badgeContent={totalAirlines} sx={badgeSx}>
              <Chip
                label={visibleAirlines[0]}
                variant="outlined"
                size="small"
                sx={chipSx}
              />
            </Badge>
          </Tooltip>
        )}

        {/* Category Chip */}
        {data?.category && (
          <Chip
            label={data?.category}
            variant="outlined"
            size="small"
            sx={chipSx}
          />
        )}

        {/* More Filters Chip */}
        {filterCount > 0 && (
          <Tooltip title={filtersTooltip} arrow placement="top">
            <Chip
              label={`+${filterCount} Filters`}
              size="small"
              variant="outlined"
              sx={{
                ...chipSx,
                borderColor: "divider",
              }}
            />
          </Tooltip>
        )}

        {/* Sort Chip */}
        {data?.sortBy && data.sortBy !== 'default' && (
          <Chip
            label={`${SORT_ICONS[data.sortBy] ?? ''} ${SORT_LABELS[data.sortBy] ?? data.sortBy}`}
            size="small"
            variant="outlined"
            sx={{
              ...chipSx,
              borderColor: '#ffaf00',
              color: '#856404',
              backgroundColor: '#fff9eb',
              fontWeight: 600,
              '& .MuiChip-label': { px: 0.8 },
            }}
          />
        )}
      </Stack>
    </Box>
  );
};

export default FilterChips;